import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import ResourcePicker from '../components/ResourcePicker';
import StandardPicker from '../components/StandardPicker';
import OptionTagPicker from '../components/OptionTagPicker';
import { PPE_OPTIONS, COMPETENCY_OPTIONS } from '../components/qmsOptionLists';
import ProcedureAuthoringMetadata from '../components/ProcedureAuthoringMetadata';
import ProcedureRevisionBar from '../components/ProcedureRevisionBar';

const API_BASE = process.env.REACT_APP_API_URL;
const TEST_TYPES = ['Firmware Flash','Functional Test','Burn-In','Calibration','Visual Inspection',
  'Electrical Safety','Performance Test','Environmental Test','Software Test','Integration Test','Generic'];
const EVIDENCE_TYPES = ['Pass / Fail','Measurement','Photo','Log File','Signature','None'];
const SUPPLIERS = ['gpv','ubiqconn','acmepoint','hk_ringer'];
const STATUS_COLORS = { Draft:'#94a3b8','In Review':'#60a5fa',Released:'#46BB89',Superseded:'#f59e0b',Obsolete:'#EC2124' };
const TRANSITIONS = { submit:{from:'Draft',to:'In Review',label:'Submit for Review'},revise:{from:'Released',to:'Draft',label:'Revise / New Draft'},approve:{from:'In Review',to:'Released',label:'Approve & Release'},reject:{from:'In Review',to:'Draft',label:'Reject'},obsolete:{from:null,to:'Obsolete',label:'Mark Obsolete'} };
const BLANK_STEP = { instruction:'',expectedResult:'',acceptanceCriteria:'',evidenceRequired:false,evidenceType:'Pass / Fail',minValue:'',maxValue:'',unit:'',tolerance:'',measurementUncertainty:'',warningNote:'' };
const SAMPLING_METHODS = ['100%','Sample','First-article','N/A'];
const TACCENT = '#34d399';

function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}
function StatusBadge({ status }) {
  const c = STATUS_COLORS[status]||'#666';
  return <span style={{ display:'inline-block',padding:'2px 10px',borderRadius:20,fontSize:11,
    fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',background:c+'22',color:c }}>{status}</span>;
}

export default function TestLibraryDetail() {
  const { id } = useParams(); const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedRevision = searchParams.get('revision') || '';
  const [availableRevisions, setAvailableRevisions] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadSequence = useRef(0);
  const { getToken } = useAuth(); const { user } = useUser();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const canManage = ['admin','quality-manager','production-engineer','service'].includes(role);
  const supplierKey = ['gpv','ubiqconn','acmepoint','hk_ringer'].includes(String(role||'').replace(/^test-/,''));

  const [entry, setEntry] = useState(null);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [error, setError] = useState('');
  const [usedInFlows, setUsedInFlows] = useState([]);
  const [tagInput, setTagInput] = useState('');

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true); setError(''); setEditing(false);
    try {
      const token = await getToken();
      const query = requestedRevision ? `?revision=${encodeURIComponent(requestedRevision)}` : '';
      const res = await window.fetch(`${API_BASE}/quality/test-library/${id}${query}`, { headers:{ Authorization:`Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || data.error || 'Could not load document');
      if (sequence === loadSequence.current) {
        setEntry(data.entry); setForm(JSON.parse(JSON.stringify(data.entry)));
        setAvailableRevisions(data.availableRevisions || []); setSnapshot(data.snapshot || null);
      }
    } catch (err) { if (sequence === loadSequence.current) { setError(err.message); setEntry(null); } }
    finally { if (sequence === loadSequence.current) setLoading(false); }
  }, [id, getToken, requestedRevision]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!id) return;
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/process-flows/used-in?resource=test&id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()).then(d => { if (d.ok) setUsedInFlows(d.flows || []); }).catch(() => {})
    );
  }, [id, getToken]);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const addTag = t => { const v=t.trim(); if(!v||(form.applicableProducts||[]).includes(v)) return; set('applicableProducts',[...(form.applicableProducts||[]),v]); setTagInput(''); };
  const removeTag = t => set('applicableProducts',(form.applicableProducts||[]).filter(x=>x!==t));
  const toggleSupplier = s => {
    const cur = form.visibility?.suppliers||[];
    const next = cur.includes(s)?cur.filter(x=>x!==s):[...cur,s];
    setForm(f=>({...f,visibility:{...f.visibility,suppliers:next,supplierAccess:next.length?'Specific':'None'}}));
  };
  const addStep = () => setForm(f=>({...f,steps:[...(f.steps||[]),{...BLANK_STEP,stepNumber:(f.steps||[]).length+1}]}));
  const removeStep = i => setForm(f=>({ ...f, steps: f.steps.filter((_,j)=>j!==i).map((s,j)=>({...s,stepNumber:j+1})) }));
  const setStep = (i,k,v) => setForm(f=>({ ...f, steps: f.steps.map((s,j)=>j===i?{...s,[k]:v}:s) }));

  const handleSave = async () => {
    if (requestedRevision || snapshot || entry?.currentStatus !== 'Draft') return;
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const payload = { ...form, changeSummary, expectedVersion:entry.__v,
        retestLimit: form.retestLimit === '' || form.retestLimit == null ? null : Number(form.retestLimit),
        effectiveDate: form.effectiveDate || null,
        nextReviewDate: form.nextReviewDate || null,
        samplingPlan: { method: form.samplingPlan?.method || '100%',
          sampleSize: form.samplingPlan?.sampleSize ? Number(form.samplingPlan.sampleSize) : null,
          aql: form.samplingPlan?.aql || '' },
        recordsRetention: { retentionPeriodMonths: form.recordsRetention?.retentionPeriodMonths ? Number(form.recordsRetention.retentionPeriodMonths) : null,
          storageLocation: form.recordsRetention?.storageLocation || '' } };
      const res = await window.fetch(`${API_BASE}/quality/test-library/${id}`, {
        method:'PUT', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message||data.error||'Save failed');
      setEntry(data.entry); setForm(JSON.parse(JSON.stringify(data.entry)));
      setEditing(false); setShowSaveModal(false); setChangeSummary('');
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleAction = async action => {
    if (requestedRevision || snapshot) return;
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/test-library/${id}/submit`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({ action, changeSummary: actionNote, expectedVersion:entry.__v }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message||data.error||'Action failed');
      setEntry(data.entry); setForm(JSON.parse(JSON.stringify(data.entry)));
      setActionModal(null); setActionNote('');
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const availableActions = entry && !requestedRevision && !snapshot ? Object.entries(TRANSITIONS)
    .filter(([,t]) => !t.from || t.from === entry.currentStatus)
    .map(([key,t]) => ({ key, label:t.label }))
    .filter(a => canManage
      || (supplierKey && a.key === 'submit' && entry?.createdBy === user?.id && entry?.currentStatus !== 'Released')
      || (supplierKey && a.key === 'revise' && entry?.currentStatus === 'Released')) : [];
  const canEditThis = !requestedRevision && !snapshot && entry?.currentStatus === 'Draft'
    && (canManage || (supplierKey && (entry?.createdBy === user?.id
      || ((entry?.visibility?.suppliers || []).includes(String(role).replace(/^test-/,''))
        && (entry?.revisions || []).some(r => r.status === 'Released')))));
  const unresolvedBlockers = (entry?.releaseBlockers || []).some(b => !b.resolved);

  if (loading) return <div style={{ padding:40,textAlign:'center',color:'var(--ni-text-muted)' }}>Loading…</div>;
  if (!entry) return <div role="alert" style={{ padding:30 }}>{error || 'Document not found.'} <button onClick={load}>Retry</button></div>;

  const inp = { background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'8px 12px',fontSize:13,width:'100%',boxSizing:'border-box' };
  const labelS = { display:'block',fontSize:11,fontWeight:700,letterSpacing:'0.06em',
    textTransform:'uppercase',color:'var(--ni-text-muted)',marginBottom:5 };
  const readVal = v => <span style={{ fontSize:14 }}>{v||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</span>;
  const displaySteps = editing ? form.steps||[] : entry.steps||[];

  return (
    <div style={{ maxWidth:820 }}>
      {/* Header */}
      <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:6 }}>
        <button onClick={()=>navigate('/quality/test-library')}
          style={{ background:'none',border:'none',color:'var(--ni-text-muted)',cursor:'pointer',fontSize:20,padding:0 }}>←</button>
        <div>
          <div style={{ fontSize:11,fontFamily:'monospace',color:'#34d399',marginBottom:2 }}>{entry.documentNumber}</div>
          <h2 style={{ margin:0,fontSize:20,fontWeight:800 }}>{entry.name}</h2>
        </div>
        <div style={{ marginLeft:'auto',display:'flex',gap:8,alignItems:'center' }}>
          <StatusBadge status={entry.currentStatus} />
          <span style={{ fontSize:12,color:'var(--ni-text-muted)',fontFamily:'monospace' }}>Rev. {entry.currentRevision}</span>
        </div>
      </div>

      {error && <div style={{ color:'#EC2124',fontSize:13,margin:'12px 0',padding:'8px 12px',background:'#EC212411',borderRadius:6 }}>{error}</div>}

      <ProcedureRevisionBar entry={entry} requestedRevision={requestedRevision} revisions={availableRevisions} snapshot={snapshot}
        onRevisionChange={revision => setSearchParams(revision ? { revision } : {})} />
      <ProcedureAuthoringMetadata value={editing ? form : entry} editing={editing} onChange={set} staff={canManage} />

      {/* Action bar */}
      {(canEditThis || availableActions.length > 0) && (
        <div style={{ display:'flex',gap:8,marginBottom:20,flexWrap:'wrap' }}>
          {!editing ? (
            <>
              {canEditThis && <button onClick={()=>setEditing(true)} disabled={!canEditThis}
                style={{ background:'#34d39922',color:'#34d399',border:'1px solid #34d39944',borderRadius:6,
                  padding:'6px 16px',fontSize:12,fontWeight:700,cursor:'pointer' }}>Edit</button>}
              {availableActions.map(a=>(
                <button key={a.key} disabled={saving || (a.key === 'approve' && unresolvedBlockers)} title={a.key === 'approve' && unresolvedBlockers ? 'Resolve release blockers before approval' : ''} onClick={()=>{setActionModal(a.key);setActionNote('');}}
                  style={{ background:'rgba(255,255,255,0.05)',border:'1px solid var(--ni-border-soft)',
                    color:'var(--ni-text)',borderRadius:6,padding:'6px 16px',fontSize:12,fontWeight:600,cursor:'pointer' }}>
                  {a.label}
                </button>
              ))}
            </>
          ) : (
            <>
              <button onClick={()=>setShowSaveModal(true)}
                style={{ background:'#46BB89',color:'#000',border:'none',borderRadius:6,
                  padding:'6px 16px',fontSize:12,fontWeight:700,cursor:'pointer' }}>Save Changes</button>
              <button onClick={()=>{ setEditing(false); setForm(JSON.parse(JSON.stringify(entry))); }}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',
                  borderRadius:6,padding:'6px 16px',fontSize:12,cursor:'pointer' }}>Cancel</button>
            </>
          )}
        </div>
      )}

      {/* Metadata */}
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:16 }}>
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:14 }}>
          <div style={labelS}>Test Type</div>
          {editing ? <select value={form.testType||''} onChange={e=>set('testType',e.target.value)} style={inp}>
            {TEST_TYPES.map(t=><option key={t}>{t}</option>)}</select>
          : readVal(entry.testType)}
        </div>
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:14 }}>
          <div style={labelS}>Duration</div>
          {editing ? <input type="number" value={form.estimatedDurationMinutes||''} onChange={e=>set('estimatedDurationMinutes',e.target.value)} style={inp} />
          : <span style={{ fontSize:14 }}>{entry.estimatedDurationMinutes?`${entry.estimatedDurationMinutes} min`:<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</span>}
        </div>
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:14 }}>
          <div style={labelS}>Steps</div>
          <span style={{ fontSize:20,fontWeight:800,color:'#34d399' }}>{entry.steps?.length||0}</span>
        </div>
      </div>

      {/* Description */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ marginBottom:12 }}>
          <div style={labelS}>Description</div>
          {editing ? <textarea value={form.description||''} onChange={e=>set('description',e.target.value)}
            rows={3} style={{ ...inp,resize:'vertical' }} />
          : <p style={{ margin:0,fontSize:14,lineHeight:1.6 }}>{entry.description||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</p>}
        </div>
        <div style={{ marginBottom:12 }}>
          <div style={labelS}>Pass Criteria</div>
          {editing ? <textarea value={form.passCriteria||''} onChange={e=>set('passCriteria',e.target.value)}
            rows={2} style={{ ...inp,resize:'vertical' }} />
          : <p style={{ margin:0,fontSize:14,lineHeight:1.6 }}>{entry.passCriteria||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</p>}
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
          <div><div style={labelS}>Firmware Version</div>
            {editing ? <input value={form.firmwareVersion||''} onChange={e=>set('firmwareVersion',e.target.value)} style={inp} />
            : readVal(entry.firmwareVersion)}</div>
          <div><div style={labelS}>Script Version</div>
            {editing ? <input value={form.testScriptVersion||''} onChange={e=>set('testScriptVersion',e.target.value)} style={inp} />
            : readVal(entry.testScriptVersion)}</div>
        </div>
      </div>

      {/* Procedure governance & acceptance */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ ...labelS,marginBottom:12 }}>Procedure Governance &amp; Acceptance</div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
          <div><div style={labelS}>Purpose</div>
            {editing ? <textarea value={form.purpose||''} onChange={e=>set('purpose',e.target.value)} rows={2} style={{ ...inp,resize:'vertical' }} /> : <p style={{ margin:0,fontSize:14 }}>{entry.purpose||'—'}</p>}</div>
          <div><div style={labelS}>Scope</div>
            {editing ? <textarea value={form.scope||''} onChange={e=>set('scope',e.target.value)} rows={2} style={{ ...inp,resize:'vertical' }} /> : <p style={{ margin:0,fontSize:14 }}>{entry.scope||'—'}</p>}</div>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
          <div><div style={labelS}>Prerequisites</div>
            {editing ? <textarea value={form.prerequisites||''} onChange={e=>set('prerequisites',e.target.value)} rows={2} style={{ ...inp,resize:'vertical' }} /> : <p style={{ margin:0,fontSize:14 }}>{entry.prerequisites||'—'}</p>}</div>
          <div><div style={labelS}>Environmental Conditions</div>
            {editing ? <input value={form.environmentalConditions||''} onChange={e=>set('environmentalConditions',e.target.value)} style={inp} /> : readVal(entry.environmentalConditions)}</div>
        </div>
        <div style={{ marginBottom:12 }}><div style={labelS}>Safety Precautions</div>
          {editing ? <input value={form.safetyPrecautions||''} onChange={e=>set('safetyPrecautions',e.target.value)} style={inp} /> : readVal(entry.safetyPrecautions)}</div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
          <div><div style={labelS}>Required PPE</div>
            {editing ? <OptionTagPicker value={form.requiredPPE||[]} onChange={v=>set('requiredPPE',v)} options={PPE_OPTIONS} accent={TACCENT} placeholder="+ Add PPE…" /> : <span style={{ fontSize:14 }}>{(entry.requiredPPE||[]).join(', ')||'—'}</span>}</div>
          <div><div style={labelS}>Required Competencies</div>
            {editing ? <OptionTagPicker value={form.requiredCompetencies||[]} onChange={v=>set('requiredCompetencies',v)} options={COMPETENCY_OPTIONS} accent={TACCENT} placeholder="+ Add competency…" /> : <span style={{ fontSize:14 }}>{(entry.requiredCompetencies||[]).join(', ')||'—'}</span>}</div>
        </div>
        <div style={{ marginBottom:12 }}><div style={labelS}>Referenced Standards</div>
          {editing ? <StandardPicker multi value={form.referencedStandards||[]} onChange={v=>set('referencedStandards',v)} getToken={getToken} accent={TACCENT} /> : <span style={{ fontSize:14 }}>{(entry.referencedStandards||[]).join(', ')||'—'}</span>}</div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:12 }}>
          <div><div style={labelS}>Sampling Method</div>
            {editing ? <select value={form.samplingPlan?.method||'100%'} onChange={e=>set('samplingPlan',{...(form.samplingPlan||{}),method:e.target.value})} style={inp}>{SAMPLING_METHODS.map(m=><option key={m}>{m}</option>)}</select> : readVal(entry.samplingPlan?.method)}</div>
          <div><div style={labelS}>Sample Size</div>
            {editing ? <input type="number" value={form.samplingPlan?.sampleSize??''} onChange={e=>set('samplingPlan',{...(form.samplingPlan||{}),sampleSize:e.target.value})} style={inp} /> : readVal(entry.samplingPlan?.sampleSize)}</div>
          <div><div style={labelS}>AQL</div>
            {editing ? <input value={form.samplingPlan?.aql||''} onChange={e=>set('samplingPlan',{...(form.samplingPlan||{}),aql:e.target.value})} style={inp} /> : readVal(entry.samplingPlan?.aql)}</div>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr',gap:12,marginBottom:12 }}>
          <div><div style={labelS}>On Failure — Reaction</div>
            {editing ? <input value={form.onFailureAction||''} onChange={e=>set('onFailureAction',e.target.value)} placeholder="e.g. quarantine + raise NCR" style={inp} /> : readVal(entry.onFailureAction)}</div>
          <div><div style={labelS}>Retest Limit</div>
            {editing ? <input type="number" value={form.retestLimit??''} onChange={e=>set('retestLimit',e.target.value)} style={inp} /> : readVal(entry.retestLimit)}</div>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
          <div><div style={labelS}>Effective Date</div>
            {editing ? <input type="date" value={form.effectiveDate?String(form.effectiveDate).slice(0,10):''} onChange={e=>set('effectiveDate',e.target.value)} style={inp} /> : <span style={{ fontSize:14 }}>{entry.effectiveDate?new Date(entry.effectiveDate).toLocaleDateString():'—'}</span>}</div>
          <div><div style={labelS}>Next Review Date</div>
            {editing ? <input type="date" value={form.nextReviewDate?String(form.nextReviewDate).slice(0,10):''} onChange={e=>set('nextReviewDate',e.target.value)} style={inp} /> : <span style={{ fontSize:14 }}>{entry.nextReviewDate?new Date(entry.nextReviewDate).toLocaleDateString():'—'}</span>}</div>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
          <div><div style={labelS}>Records Retention (months)</div>
            {editing ? <input type="number" value={form.recordsRetention?.retentionPeriodMonths??''} onChange={e=>set('recordsRetention',{...(form.recordsRetention||{}),retentionPeriodMonths:e.target.value})} style={inp} /> : readVal(entry.recordsRetention?.retentionPeriodMonths)}</div>
          <div><div style={labelS}>Records Storage Location</div>
            {editing ? <input value={form.recordsRetention?.storageLocation||''} onChange={e=>set('recordsRetention',{...(form.recordsRetention||{}),storageLocation:e.target.value})} style={inp} /> : readVal(entry.recordsRetention?.storageLocation)}</div>
        </div>
      </div>

      {/* Approval — prepared / reviewed / approved sign-off */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ ...labelS,marginBottom:12 }}>Approval (Prepared / Reviewed / Approved)</div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12 }}>
          <div><div style={labelS}>Prepared by</div>
            {editing ? <input value={form.approval?.preparedByName||''} onChange={e=>set('approval',{...(form.approval||{}),preparedByName:e.target.value})} style={inp} /> : readVal(entry.approval?.preparedByName)}</div>
          <div><div style={labelS}>Reviewed by</div>
            {editing ? <input value={form.approval?.reviewedByName||''} onChange={e=>set('approval',{...(form.approval||{}),reviewedByName:e.target.value})} style={inp} /> : readVal(entry.approval?.reviewedByName)}</div>
          <div><div style={labelS}>Approved by</div>
            {editing ? <input value={form.approval?.approvedByName||''} onChange={e=>set('approval',{...(form.approval||{}),approvedByName:e.target.value})} style={inp} /> : readVal(entry.approval?.approvedByName)}</div>
        </div>
      </div>

      {/* Test Steps */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14 }}>
          <div style={labelS}>Test Steps</div>
          {editing && (
            <button type="button" onClick={addStep}
              style={{ background:'#34d39922',color:'#34d399',border:'1px solid #34d39944',borderRadius:6,
                padding:'4px 14px',fontSize:12,fontWeight:700,cursor:'pointer' }}>+ Step</button>
          )}
        </div>
        {displaySteps.length===0 ? (
          <div style={{ fontSize:14,color:'var(--ni-text-muted)',textAlign:'center',padding:'20px 0' }}>No test steps defined.</div>
        ) : displaySteps.map((step,i)=>(
          <div key={i} style={{ border:'1px solid var(--ni-border-soft)',borderRadius:8,padding:14,marginBottom:10,
            background:'rgba(52,211,153,0.03)' }}>
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10 }}>
              <span style={{ fontSize:12,fontWeight:700,color:'#34d399',letterSpacing:'0.05em' }}>STEP {step.stepNumber}</span>
              {editing && <button type="button" onClick={()=>removeStep(i)}
                style={{ background:'none',border:'none',color:'#EC2124',cursor:'pointer',fontSize:16 }}>×</button>}
            </div>
            {editing ? (
              <>
                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:8 }}>
                  <div><div style={labelS}>Instruction</div>
                    <textarea value={step.instruction} onChange={e=>setStep(i,'instruction',e.target.value)}
                      rows={2} style={{ ...inp,resize:'vertical' }} /></div>
                  <div><div style={labelS}>Expected Result</div>
                    <textarea value={step.expectedResult} onChange={e=>setStep(i,'expectedResult',e.target.value)}
                      rows={2} style={{ ...inp,resize:'vertical' }} /></div>
                </div>
                <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr',gap:10,marginBottom:8 }}>
                  <div><div style={labelS}>Acceptance Criteria</div>
                    <input value={step.acceptanceCriteria} onChange={e=>setStep(i,'acceptanceCriteria',e.target.value)} style={inp} /></div>
                  <div><div style={labelS}>Evidence</div>
                    <select value={step.evidenceType} onChange={e=>setStep(i,'evidenceType',e.target.value)} style={inp}>
                      {EVIDENCE_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
                </div>
                {step.evidenceType==='Measurement' && (
                  <>
                    <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:8 }}>
                      <div><div style={labelS}>Min</div><input type="number" value={step.minValue||''} onChange={e=>setStep(i,'minValue',e.target.value)} style={inp} /></div>
                      <div><div style={labelS}>Max</div><input type="number" value={step.maxValue||''} onChange={e=>setStep(i,'maxValue',e.target.value)} style={inp} /></div>
                      <div><div style={labelS}>Unit</div><input value={step.unit||''} onChange={e=>setStep(i,'unit',e.target.value)} style={inp} /></div>
                    </div>
                    <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:8 }}>
                      <div><div style={labelS}>Tolerance</div><input value={step.tolerance||''} onChange={e=>setStep(i,'tolerance',e.target.value)} placeholder="e.g. ±0.2 V" style={inp} /></div>
                      <div><div style={labelS}>Measurement Uncertainty</div><input value={step.measurementUncertainty||''} onChange={e=>setStep(i,'measurementUncertainty',e.target.value)} placeholder="e.g. ±0.05 V (k=2)" style={inp} /></div>
                    </div>
                  </>
                )}
                <div><div style={labelS}>Warning / Note</div>
                  <input value={step.warningNote||''} onChange={e=>setStep(i,'warningNote',e.target.value)}
                    placeholder="Safety note (optional)" style={inp} /></div>
              </>
            ) : (
              <div>
                <p style={{ margin:'0 0 8px',fontSize:14,lineHeight:1.6 }}><strong>Instruction:</strong> {step.instruction||'—'}</p>
                {step.expectedResult && <p style={{ margin:'0 0 6px',fontSize:14 }}><strong>Expected:</strong> {step.expectedResult}</p>}
                {step.acceptanceCriteria && <p style={{ margin:'0 0 6px',fontSize:14 }}><strong>Criteria:</strong> {step.acceptanceCriteria}</p>}
                <div style={{ display:'flex',gap:16,flexWrap:'wrap',marginTop:4 }}>
                  <span style={{ fontSize:12,color:'var(--ni-text-muted)' }}>Evidence: <strong style={{ color:'var(--ni-text)' }}>{step.evidenceType}</strong></span>
                  {step.evidenceType==='Measurement' && (step.minValue||step.maxValue) && (
                    <span style={{ fontSize:12,color:'var(--ni-text-muted)' }}>
                      Range: <strong style={{ color:'var(--ni-text)' }}>{step.minValue??'?'} – {step.maxValue??'?'} {step.unit}</strong>
                    </span>
                  )}
                  {step.tolerance && <span style={{ fontSize:12,color:'var(--ni-text-muted)' }}>Tol: <strong style={{ color:'var(--ni-text)' }}>{step.tolerance}</strong></span>}
                  {step.measurementUncertainty && <span style={{ fontSize:12,color:'var(--ni-text-muted)' }}>U: <strong style={{ color:'var(--ni-text)' }}>{step.measurementUncertainty}</strong></span>}
                </div>
                {step.warningNote && (
                  <div style={{ marginTop:8,padding:'6px 10px',background:'#f59e0b11',border:'1px solid #f59e0b33',borderRadius:6,fontSize:12,color:'#f59e0b' }}>
                    ⚠ {step.warningNote}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Notes */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Notes</div>
        {editing ? <textarea value={form.notes||''} onChange={e=>set('notes',e.target.value)}
          rows={2} style={{ ...inp,resize:'vertical' }} />
        : <p style={{ margin:0,fontSize:14,lineHeight:1.6 }}>{entry.notes||<span style={{ color:'var(--ni-text-muted)' }}>—</span>}</p>}
      </div>

      {/* Supplier visibility */}
      {canManage && (
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
          <div style={labelS}>Supplier Visibility</div>
          <div style={{ display:'flex',gap:8,flexWrap:'wrap',marginTop:4 }}>
            {SUPPLIERS.map(s=>(
              editing ? (
                <button type="button" key={s} onClick={()=>toggleSupplier(s)}
                  style={{ padding:'5px 14px',borderRadius:20,fontSize:12,fontWeight:700,cursor:'pointer',border:'1px solid',
                    background:(form.visibility?.suppliers||[]).includes(s)?'#34d39922':'transparent',
                    color:(form.visibility?.suppliers||[]).includes(s)?'#34d399':'var(--ni-text-muted)',
                    borderColor:(form.visibility?.suppliers||[]).includes(s)?'#34d399':'var(--ni-border-soft)' }}>
                  {s.toUpperCase()}
                </button>
              ) : (
                <span key={s} style={{ padding:'3px 12px',borderRadius:20,fontSize:11,fontWeight:700,
                  background:(entry.visibility?.suppliers||[]).includes(s)?'#34d39922':'rgba(255,255,255,0.04)',
                  color:(entry.visibility?.suppliers||[]).includes(s)?'#34d399':'var(--ni-text-muted)' }}>
                  {s.toUpperCase()}
                </span>
              )
            ))}
          </div>
        </div>
      )}

      {/* Applicable Products */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Applicable Products</div>
        <div style={{ display:'flex',gap:6,flexWrap:'wrap',marginBottom:editing?8:0,marginTop:4 }}>
          {(editing?form:entry).applicableProducts?.map(t=>(
            <span key={t} style={{ padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:700,
              background:'#34d39922',color:'#34d399',display:'flex',alignItems:'center',gap:4 }}>
              {t}
              {editing && <button onClick={()=>removeTag(t)} style={{ background:'none',border:'none',color:'inherit',cursor:'pointer',padding:0,fontSize:12,lineHeight:1 }}>×</button>}
            </span>
          ))}
          {!editing&&(!entry.applicableProducts?.length)&&<span style={{ fontSize:12,color:'var(--ni-text-muted)' }}>—</span>}
        </div>
        {editing && (
          <div style={{ display:'flex',gap:8 }}>
            <input value={tagInput} onChange={e=>setTagInput(e.target.value)}
              onKeyDown={e=>{ if(e.key==='Enter'){e.preventDefault();addTag(tagInput);}}}
              placeholder="Type product name, press Enter"
              style={{ ...inp,flex:1 }} />
            <button type="button" onClick={()=>addTag(tagInput)}
              style={{ background:'#34d39922',color:'#34d399',border:'1px solid #34d39944',borderRadius:6,padding:'6px 12px',fontSize:12,cursor:'pointer' }}>Add</button>
          </div>
        )}
      </div>

      {/* Required Equipment */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Required Equipment</div>
        <ResourcePicker
          endpoint="/quality/equipment" responseKey="items"
          items={editing ? form.requiredEquipment||[] : entry.requiredEquipment||[]}
          onChange={v=>set('requiredEquipment',v)}
          getToken={getToken} color="#f59e0b" label="equipment"
          disabled={!editing} detailPath="/quality/equipment"
        />
      </div>

      {/* Required Jigs */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Required Jigs</div>
        <ResourcePicker
          endpoint="/quality/jigs" responseKey="jigs"
          items={editing ? form.requiredJigs||[] : entry.requiredJigs||[]}
          onChange={v=>set('requiredJigs',v)}
          getToken={getToken} color="#a78bfa" label="jigs"
          disabled={!editing} detailPath="/quality/jigs"
        />
      </div>

      {/* Used In Flows */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
        <div style={labelS}>Used In Process Flows</div>
        {usedInFlows.length===0
          ? <div style={{ fontSize:13,color:'var(--ni-text-muted)',marginTop:4 }}>Not linked to any flow yet.</div>
          : usedInFlows.map(f=>(
            <div key={f._id} onClick={()=>navigate(`/quality/process/${f._id}`)}
              style={{ display:'flex',gap:10,alignItems:'center',padding:'7px 0',
                borderBottom:'1px solid var(--ni-border-soft)',cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.opacity='0.75'}
              onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
              <span style={{ fontFamily:'monospace',fontSize:11,color:'#60a5fa',flexShrink:0 }}>{f.documentNumber}</span>
              <span style={{ fontSize:13,flex:1 }}>{f.name}</span>
              <StatusBadge status={f.currentStatus}/>
            </div>
          ))
        }
      </div>

      {/* Original source evidence */}
      {entry.source?.url && (
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
          <div style={labelS}>Original source evidence</div>
          <a href={entry.source.url} target="_blank" rel="noopener noreferrer"
            style={{ fontSize:13,color:'#34d399',textDecoration:'none',wordBreak:'break-word' }}>
            {(entry.source.system||'SharePoint')}{entry.source.fileName?` — ${entry.source.fileName}`:''} ↗
          </a>
          {entry.source.externalModifiedAt && <div style={{ fontSize:11,color:'var(--ni-text-muted)',marginTop:4 }}>Updated {new Date(entry.source.externalModifiedAt).toLocaleDateString()}</div>}
        </div>
      )}

      {/* Revision history */}
      <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16 }}>
        <div style={labelS}>Revision History</div>
        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12,marginTop:8 }}>
          <thead>
            <tr style={{ color:'var(--ni-text-muted)',fontSize:10,textTransform:'uppercase',letterSpacing:'0.06em' }}>
              {['Rev','Status','Summary','By','Date'].map(h=>(
                <th key={h} style={{ padding:'6px 10px',textAlign:'left',fontWeight:600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...(entry.revisions||[])].reverse().map((r,i)=>(
              <tr key={i} style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
                <td style={{ padding:'8px 10px',fontFamily:'monospace' }}>{r.revision}</td>
                <td style={{ padding:'8px 10px' }}><StatusBadge status={r.status}/></td>
                <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)' }}>{r.changeSummary}</td>
                <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)' }}>{r.updatedByName}</td>
                <td style={{ padding:'8px 10px',color:'var(--ni-text-muted)',whiteSpace:'nowrap' }}>
                  {r.updatedAt?new Date(r.updatedAt).toLocaleDateString():'—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save modal */}
      {showSaveModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'var(--ni-bg)',border:'1px solid var(--ni-border-soft)',borderRadius:12,padding:28,width:420 }}>
            <h3 style={{ margin:'0 0 16px',fontSize:16 }}>Save Changes</h3>
            <div style={labelS}>Change Summary</div>
            <input value={changeSummary} onChange={e=>setChangeSummary(e.target.value)}
              placeholder="Describe what changed…" style={{ ...inp,marginBottom:20 }} />
            <div style={{ display:'flex',gap:10 }}>
              <button onClick={handleSave} disabled={saving}
                style={{ background:'#46BB89',color:'#000',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:'pointer' }}>
                {saving?'Saving…':'Save'}
              </button>
              <button onClick={()=>setShowSaveModal(false)}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Action modal */}
      {actionModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'var(--ni-bg)',border:'1px solid var(--ni-border-soft)',borderRadius:12,padding:28,width:420 }}>
            <h3 style={{ margin:'0 0 16px',fontSize:16 }}>{TRANSITIONS[actionModal]?.label}</h3>
            <div style={labelS}>Note (optional)</div>
            <input value={actionNote} onChange={e=>setActionNote(e.target.value)}
              placeholder="Reason or comment…" style={{ ...inp,marginBottom:20 }} />
            <div style={{ display:'flex',gap:10 }}>
              <button onClick={()=>handleAction(actionModal)} disabled={saving}
                style={{ background:'#46BB89',color:'#000',border:'none',borderRadius:6,padding:'9px 20px',fontWeight:700,fontSize:13,cursor:'pointer' }}>
                {saving?'Processing…':'Confirm'}
              </button>
              <button onClick={()=>setActionModal(null)}
                style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',borderRadius:6,padding:'9px 16px',fontSize:13,cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
